/**
 * Botão da Plataforma (primitivo do redesign "admin Positiva").
 * `primary` usa o vermelho de marca; `outline`/`ghost` seguem os tokens neutros.
 * Alvo mínimo de 36–44px — acessibilidade de toque.
 */
import { createElement as h, type ButtonHTMLAttributes, type ReactElement, type ReactNode } from "react";
import { cx } from "./cx.js";

export type ButtonVariant = "primary" | "outline" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

const VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-brand text-on-brand shadow-s1 hover:bg-brand-pressed border border-transparent",
  outline: "bg-surface text-ink border border-border-strong hover:border-ink",
  ghost: "bg-transparent text-ink border border-transparent hover:bg-surface-soft",
  danger: "bg-danger-soft text-danger border border-danger hover:bg-danger hover:text-on-brand"
};
const SIZE: Record<ButtonSize, string> = {
  sm: "min-h-[36px] px-3 text-sm",
  md: "min-h-[44px] px-4 text-base",
  lg: "min-h-[44px] px-4 text-base"
};

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children?: ReactNode;
  [dataAttr: `data-${string}`]: string | number | boolean | undefined;
}

/** Classes do botão — reutilizável em `<a>`/`NavLink` que devem parecer botão. */
export function buttonClass(variant: ButtonVariant = "outline", size: ButtonSize = "md", extra?: string): string {
  return cx(
    "inline-flex items-center justify-center gap-2 rounded-control font-bold whitespace-nowrap active:translate-y-px transition-colors duration-fast ease-s2",
    "disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none",
    VARIANT[variant],
    SIZE[size],
    extra
  );
}

export function Button({ variant = "outline", size = "md", className, type = "button", children, ...rest }: ButtonProps): ReactElement {
  return h("button", { ...rest, type, className: buttonClass(variant, size, className) }, children);
}
